import {
  FALLBACK_INDUSTRY_OPTIONS,
  isPayBasis,
  type AllowedWorkTypes,
  type CompanyIndustry,
  type CompanyIndustryProfile,
  type CompensationSetupReview,
  type CreateDisciplineAssignmentInput,
  type CreateServiceRateInput,
  type DifferentialKind,
  type Discipline,
  type DisciplineAssignment,
  type DisciplineCapabilities,
  type DisciplineInput,
  type EarningDetail,
  type EmployeeRateCapabilities,
  type IndustryChangePreview,
  type IndustryKind,
  type IndustrySelection,
  type NaicsIndustrySearch,
  type NaicsSector,
  type PayBasis,
  type RateSource,
  type ServiceLine,
  type SetIndustryProfileInput,
  type ServiceRate,
  type ServiceType,
  type ServiceTypeCapabilities,
  type ServiceTypeInput,
  type SetupReviewItem,
  type SetupReviewSeverity,
} from './types';
import { scaled2ToWire } from './money';

/**
 * The seam between the wire shapes of contract section 10 and the UI types of types.ts. This is the ONLY file that knows the wire:
 * every request body is built here and every response is read here, so a screen never branches on a missing member. Nothing is
 * invented: a member the server did not send becomes null, an empty list or the stated default, never a guess.
 *
 * Enums on these endpoints travel as strings. The one exception is Compensation's base-rate `payType`, which stays an integer (the
 * legacy 1 to 5 plus the new 6 to 9), mapped by `payBasisFromPayType` / `payTypeFromPayBasis`.
 */

// ---- pay basis <-> the legacy integer pay type on Compensation's base rate ----

// Only the original six can be a BASE pay type. A basis added in universal compensation phase B (per mile, per trip ...) is a service-rate basis only: it never
// becomes an employee's base assignment, so it has no number here.
const PAY_TYPE_BY_BASIS: Partial<Record<PayBasis, number>> = { Hourly: 1, Salary: 2, PerVisit: 6, PerShift: 7, Daily: 8, PerUnit: 9 };

/** The integer Compensation stores for a basis (Hourly 1, Salary 2, Per Visit 6, Per Shift 7, Daily 8, Per Unit/Case 9). Throws for a basis that cannot be a base pay type. */
export function payTypeFromPayBasis(basis: PayBasis): number {
  const payType = PAY_TYPE_BY_BASIS[basis];
  if (payType === undefined) throw new Error(`${basis} cannot be used as a base pay type.`);
  return payType;
}

/** The basis for a Compensation pay type, or null for the legacy Commission (3), Tips (4) and Executive bonus (5), which are not bases. */
export function payBasisFromPayType(payType: number | null | undefined): PayBasis | null {
  const hit = (Object.entries(PAY_TYPE_BY_BASIS) as Array<[PayBasis, number]>).find(([, value]) => value === payType);
  return hit ? hit[0] : null;
}

// ---- raw wire shapes ----

type Raw = Record<string, unknown>;

const asString = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const asNullableString = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const asNumber = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const asNullableNumber = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const asBool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);
const asStringArray = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const asRaw = (v: unknown): Raw => (typeof v === 'object' && v !== null ? (v as Raw) : {});
const asBasisOrNull = (v: unknown): PayBasis | null => (isPayBasis(v) ? v : null);

/** A list endpoint answers `{ items: [...] }`; a bare array is read the same way (some Module 4 lists are bare). */
export function listFromWire(raw: unknown): Raw[] {
  if (Array.isArray(raw)) return raw.map(asRaw);
  const items = asRaw(raw).items;
  return Array.isArray(items) ? items.map(asRaw) : [];
}

const KINDS: readonly DifferentialKind[] = ['None', 'Amount', 'Percent'];
const asKind = (v: unknown): DifferentialKind => (KINDS.includes(v as DifferentialKind) ? (v as DifferentialKind) : 'None');

// ---- disciplines ----

export function disciplineFromWire(raw: unknown): Discipline {
  const r = asRaw(raw);
  return {
    id: asString(r.id), code: asString(r.code), name: asString(r.name), description: asString(r.description),
    industries: asStringArray(r.industries), isSystem: asBool(r.isSystem, false), isActive: asBool(r.isActive, true),
    concurrencyVersion: asNumber(r.concurrencyVersion),
  };
}

export const disciplinesFromWire = (raw: unknown): Discipline[] => listFromWire(raw).map(disciplineFromWire);

/** A create sends `isActive`; an update is a full replace of the editable fields and carries the expected version instead (the service has no `isActive` on an update: it is changed by deactivate and reactivate). */
export function disciplineToWire(input: DisciplineInput, expectedConcurrencyVersion?: number): Raw {
  const base = { name: input.name, code: input.code, description: input.description, industries: input.industries };
  return expectedConcurrencyVersion === undefined ? { ...base, isActive: input.isActive } : { ...base, expectedConcurrencyVersion };
}

export function disciplineAssignmentFromWire(raw: unknown): DisciplineAssignment {
  const r = asRaw(raw);
  return {
    id: asString(r.id), disciplineId: asString(r.disciplineId), disciplineCode: asString(r.disciplineCode), disciplineName: asString(r.disciplineName),
    isPrimary: asBool(r.isPrimary, false), effectiveFrom: asString(r.effectiveFrom), effectiveTo: asNullableString(r.effectiveTo), reason: asNullableString(r.reason),
  };
}

export const disciplineAssignmentsFromWire = (raw: unknown): DisciplineAssignment[] => listFromWire(raw).map(disciplineAssignmentFromWire);

export function createAssignmentToWire(input: CreateDisciplineAssignmentInput): Raw {
  return {
    disciplineId: input.disciplineId, isPrimary: input.isPrimary,
    ...(input.effectiveFromDate ? { effectiveFromDate: input.effectiveFromDate } : {}),
    ...(input.reason ? { reason: input.reason } : {}),
  };
}

/** `GET` and `PUT /api/employees/{id}/allowed-service-types`. A row without an id is dropped; the flag is read from the list so the two can never disagree on screen. */
export function allowedWorkTypesFromWire(raw: unknown): AllowedWorkTypes {
  const r = asRaw(raw);
  const items = listFromWire(r)
    .map((i) => ({ serviceTypeId: asString(i.serviceTypeId), code: asString(i.code), name: asString(i.name) }))
    .filter((i) => i.serviceTypeId !== '');
  return { employeeId: asString(r.employeeId), isRestricted: items.length > 0, items };
}

/** The `PUT` body: the WHOLE set of work type ids; an empty list removes the limit. */
export function allowedWorkTypesToWire(serviceTypeIds: readonly string[]): Raw {
  return { serviceTypeIds: [...serviceTypeIds] };
}

export function disciplineCapabilitiesFromWire(raw: unknown): DisciplineCapabilities {
  const r = asRaw(raw);
  return { canManageDisciplines: asBool(r.canManageDisciplines, false), canAssignDisciplines: asBool(r.canAssignDisciplines, false) };
}

// ---- service types, industry ----

export function serviceTypeFromWire(raw: unknown): ServiceType {
  const r = asRaw(raw);
  return {
    id: asString(r.id), code: asString(r.code), name: asString(r.name), description: asString(r.description), industries: asStringArray(r.industries),
    isSystem: asBool(r.isSystem, false), isActive: asBool(r.isActive, true), suggestedPayBasis: asBasisOrNull(r.suggestedPayBasis),
    concurrencyVersion: asNumber(r.concurrencyVersion),
  };
}

export const serviceTypesFromWire = (raw: unknown): ServiceType[] => listFromWire(raw).map(serviceTypeFromWire);

export function serviceTypeToWire(input: ServiceTypeInput, expectedConcurrencyVersion?: number): Raw {
  return {
    name: input.name, code: input.code, description: input.description, industries: input.industries, suggestedPayBasis: input.suggestedPayBasis, isActive: input.isActive,
    ...(expectedConcurrencyVersion === undefined ? {} : { expectedConcurrencyVersion }),
  };
}

export function serviceTypeCapabilitiesFromWire(raw: unknown): ServiceTypeCapabilities {
  const r = asRaw(raw);
  return { canManageServiceTypes: asBool(r.canManageServiceTypes, false), canManageCompanyRates: asBool(r.canManageCompanyRates, false) };
}

export function employeeRateCapabilitiesFromWire(raw: unknown): EmployeeRateCapabilities {
  return { canManageEmployeeRates: asBool(asRaw(raw).canManageEmployeeRates, false) };
}

export function companyIndustryFromWire(raw: unknown): CompanyIndustry {
  const r = asRaw(raw);
  const options = Array.isArray(r.options)
    ? r.options.map(asRaw).map((o) => ({ code: asString(o.code), label: asString(o.label, asString(o.code)) })).filter((o) => o.code !== '')
    : [];
  return { industry: asNullableString(r.industry), options: options.length > 0 ? options : [...FALLBACK_INDUSTRY_OPTIONS], updatedAtUtc: asNullableString(r.updatedAtUtc) };
}

// ---- rates ----

export function serviceRateFromWire(raw: unknown): ServiceRate {
  const r = asRaw(raw);
  const employeeId = asNullableString(r.employeeId);
  return {
    id: asString(r.id),
    ...(employeeId ? { employeeId } : {}),
    disciplineId: asNullableString(r.disciplineId), disciplineName: asNullableString(r.disciplineName),
    serviceTypeId: asNullableString(r.serviceTypeId), serviceTypeName: asNullableString(r.serviceTypeName),
    workLocationId: asNullableString(r.workLocationId),
    payBasis: asBasisOrNull(r.payBasis) ?? 'Hourly', rate: asNumber(r.rate), currency: asString(r.currency, 'USD'),
    differentialKind: asKind(r.differentialKind), differentialValue: asNullableNumber(r.differentialValue),
    description: asString(r.description), effectiveFrom: asString(r.effectiveFrom), effectiveTo: asNullableString(r.effectiveTo),
    isActive: asBool(r.isActive, asNullableString(r.effectiveTo) === null),
    concurrencyVersion: asNumber(r.concurrencyVersion),
    appliesToAnyWorkerCategory: asBool(r.appliesToAnyWorkerCategory, asNullableString(r.disciplineId) === null),
  };
}

/** `GET /api/service-lines/supported-pay-bases` (Payroll Calculation): the wire names of every pay basis it can price. Anything unreadable is an empty list. */
export function supportedPayBasesFromWire(raw: unknown): string[] {
  const list = asRaw(raw).payBases;
  if (!Array.isArray(list)) return [];
  return list.map((item) => asNullableString(asRaw(item).name)).filter((name): name is string => name !== null && name !== '');
}

export const serviceRatesFromWire = (raw: unknown): ServiceRate[] => listFromWire(raw).map(serviceRateFromWire);

/** The request body of `POST /api/service-rates` and `POST /api/employees/{id}/service-rates`. Money is written from integers (see money.ts). */
export function createServiceRateToWire(input: CreateServiceRateInput): Raw {
  const anyCategory = input.appliesToAnyWorkerCategory === true;
  return {
    ...(anyCategory ? { appliesToAnyWorkerCategory: true } : { disciplineId: input.disciplineId }),
    serviceTypeId: input.serviceTypeId,
    workLocationId: input.workLocationId,
    payBasis: input.payBasis,
    rate: scaled2ToWire(input.rateCents),
    currency: 'USD',
    differentialKind: input.differentialKind,
    differentialValue: input.differentialKind === 'None' || input.differentialUnits === null ? null : scaled2ToWire(input.differentialUnits),
    description: input.description,
    effectiveFrom: input.effectiveFrom,
    effectiveTo: input.effectiveTo,
  };
}

// ---- the pay-run service line and earningDetails ----

export function serviceLineToWire(line: ServiceLine): Raw {
  return {
    disciplineId: line.disciplineId,
    serviceTypeId: line.serviceTypeId,
    payBasis: line.payBasis,
    quantity: line.quantity,
    workedHours: line.workedHours,
    ...(line.serviceDate ? { serviceDate: line.serviceDate } : {}),
    workLocationId: line.workLocationId,
    additionalEarningAuthorized: line.additionalEarningAuthorized,
    additionalEarningReason: line.additionalEarningAuthorized ? line.additionalEarningReason : null,
  };
}

export function serviceLineFromWire(raw: unknown): ServiceLine | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = asRaw(raw);
  return {
    disciplineId: asString(r.disciplineId), serviceTypeId: asNullableString(r.serviceTypeId), payBasis: asBasisOrNull(r.payBasis) ?? 'PerVisit',
    quantity: asNumber(r.quantity), workedHours: asNullableNumber(r.workedHours), serviceDate: asNullableString(r.serviceDate),
    workLocationId: asNullableString(r.workLocationId), additionalEarningAuthorized: asBool(r.additionalEarningAuthorized, false),
    additionalEarningReason: asNullableString(r.additionalEarningReason),
    disciplineCode: asNullableString(r.disciplineCode), disciplineName: asNullableString(r.disciplineName),
    serviceTypeCode: asNullableString(r.serviceTypeCode), serviceTypeName: asNullableString(r.serviceTypeName),
  };
}

const SOURCES: readonly RateSource[] = ['EmployeeServiceRate', 'CompanyLocationRate', 'CompanyDefaultRate', 'EmployeeBaseRate', 'AdHoc', 'BaseAssignment'];

export function earningDetailFromWire(raw: unknown): EarningDetail {
  const r = asRaw(raw);
  return {
    label: asString(r.label), amount: asNumber(r.amount), payBasis: asBasisOrNull(r.payBasis), quantity: asNullableNumber(r.quantity), rate: asNullableNumber(r.rate),
    differentialKind: r.differentialKind === undefined || r.differentialKind === null ? null : asKind(r.differentialKind),
    differentialValue: asNullableNumber(r.differentialValue), effectiveRate: asNullableNumber(r.effectiveRate),
    rateSource: SOURCES.includes(r.rateSource as RateSource) ? (r.rateSource as RateSource) : null, rateRuleId: asNullableString(r.rateRuleId),
    explanation: asString(r.explanation),
    disciplineId: asNullableString(r.disciplineId), disciplineCode: asNullableString(r.disciplineCode), disciplineName: asNullableString(r.disciplineName),
    serviceTypeId: asNullableString(r.serviceTypeId), serviceTypeCode: asNullableString(r.serviceTypeCode), serviceTypeName: asNullableString(r.serviceTypeName),
    workedHours: asNullableNumber(r.workedHours), serviceDate: asNullableString(r.serviceDate),
    workLocationId: asNullableString(r.workLocationId),
    additionalEarningAuthorized: typeof r.additionalEarningAuthorized === 'boolean' ? r.additionalEarningAuthorized : null,
    additionalEarningReason: asNullableString(r.additionalEarningReason),
  };
}

/** `warnings[]` of a calculation or preview line: plain sentences the server wrote (for example an hourly base pay and an hourly service line in one calculation). */
export const warningsFromWire = (raw: unknown): string[] => (Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string' && x !== '') : []);

/** `earningDetails[]` of a calculation or preview line; an older response with none returns an empty list. */
export const earningDetailsFromWire = (raw: unknown): EarningDetail[] => (Array.isArray(raw) ? raw.map(earningDetailFromWire) : []);

// ---- the company industry profile and the NAICS reference ----

export function naicsSectorsFromWire(raw: unknown): { version: string; sectors: NaicsSector[] } {
  const r = asRaw(raw);
  const sectors = (Array.isArray(r.sectors) ? r.sectors : []).map(asRaw).map((s) => ({ code: asString(s.code), title: asString(s.title) })).filter((s) => s.code !== '');
  return { version: asString(r.version), sectors };
}

export function naicsSearchFromWire(raw: unknown): NaicsIndustrySearch {
  const r = asRaw(raw);
  const items = (Array.isArray(r.items) ? r.items : []).map(asRaw)
    .map((i) => ({ code: asString(i.code), title: asString(i.title), sectorCode: asString(i.sectorCode), sectorTitle: asString(i.sectorTitle) }))
    .filter((i) => i.code !== '');
  return { version: asString(r.version), items, hasMore: asBool(r.hasMore, false) };
}

const INDUSTRY_KINDS: readonly IndustryKind[] = ['Chosen', 'NotSure', 'Custom'];

function industrySelectionFromWire(raw: unknown): IndustrySelection {
  const r = asRaw(raw);
  const kind = INDUSTRY_KINDS.find((k) => k === r.kind) ?? 'Chosen';
  return {
    kind, sectorCode: asNullableString(r.sectorCode), sectorTitle: asNullableString(r.sectorTitle), detailCode: asNullableString(r.detailCode),
    detailTitle: asNullableString(r.detailTitle), customDescription: asNullableString(r.customDescription), healthcareTemplate: asNullableString(r.healthcareTemplate),
    healthcareTemplateLabel: asNullableString(r.healthcareTemplateLabel),
  };
}

export function companyIndustryProfileFromWire(raw: unknown): CompanyIndustryProfile {
  const r = asRaw(raw);
  const templates = (Array.isArray(r.healthcareTemplates) ? r.healthcareTemplates : []).map(asRaw)
    .map((o) => ({ code: asString(o.code), label: asString(o.label, asString(o.code)) })).filter((o) => o.code !== '');
  const source = r.source === 'Profile' || r.source === 'LegacySetting' ? r.source : 'None';
  return {
    primary: r.primary == null ? null : industrySelectionFromWire(r.primary),
    additional: (Array.isArray(r.additional) ? r.additional : []).map(industrySelectionFromWire),
    naicsVersion: asNullableString(r.naicsVersion),
    needsReview: asBool(r.needsReview, false),
    isHealthcare: asBool(r.isHealthcare, false),
    suggestionTags: asStringArray(r.suggestionTags),
    healthcareTemplates: templates.length > 0 ? templates : FALLBACK_INDUSTRY_OPTIONS.filter((o) => o.code !== 'GeneralNonHealthcare').map((o) => ({ ...o })),
    source,
    concurrencyVersion: asNumber(r.concurrencyVersion),
    updatedAtUtc: asNullableString(r.updatedAtUtc),
  };
}

/** `GET /api/compensation-setup-review`. Tolerant: an unknown severity reads as "Info" (never as a problem), a missing list is empty, and the summary is counted from the items so it can never disagree with them. */
export function compensationSetupReviewFromWire(raw: unknown): CompensationSetupReview {
  const r = asRaw(raw);
  const industry = asRaw(r.industry);
  const workTypes = asRaw(r.workTypes);
  const rates = asRaw(r.companyRates);
  const severity = (v: unknown): SetupReviewSeverity => (v === 'Ok' || v === 'Review' ? v : 'Info');
  const items: SetupReviewItem[] = (Array.isArray(r.items) ? r.items : []).map(asRaw).map((i) => ({
    key: asString(i.key), area: asString(i.area), label: asString(i.label), severity: severity(i.severity), message: asString(i.message),
    count: asNullableNumber(i.count), names: asStringArray(i.names),
  }));
  const source = industry.source === 'Profile' || industry.source === 'LegacySetting' ? industry.source : 'None';
  return {
    rulesVersion: asNumber(r.rulesVersion, 1),
    asOf: asString(r.asOf),
    industry: { source, needsReview: asBool(industry.needsReview, false), isHealthcare: asBool(industry.isHealthcare, false), label: asNullableString(industry.label) },
    workTypes: {
      systemActive: asNumber(workTypes.systemActive), systemInactive: asNumber(workTypes.systemInactive), companyActive: asNumber(workTypes.companyActive), companyInactive: asNumber(workTypes.companyInactive),
    },
    companyRates: {
      active: asNumber(rates.active),
      byPayBasis: (Array.isArray(rates.byPayBasis) ? rates.byPayBasis : []).map(asRaw).map((b) => ({ payBasis: asString(b.payBasis), count: asNumber(b.count) })).filter((b) => b.payBasis !== ''),
      beyondOriginalFive: asNumber(rates.beyondOriginalFive),
    },
    items,
    summary: { review: items.filter((i) => i.severity === 'Review').length, info: items.filter((i) => i.severity === 'Info').length, ok: items.filter((i) => i.severity === 'Ok').length },
    notIncluded: (Array.isArray(r.notIncluded) ? r.notIncluded : []).map(asRaw).map((n) => ({ area: asString(n.area), label: asString(n.label), whereToLook: asString(n.whereToLook) })),
  };
}

/** `POST /api/company-industry-profile/preview`. The added and removed tags and the flag are read from the two tag lists, so they can never disagree on screen. */
export function industryChangePreviewFromWire(raw: unknown): IndustryChangePreview {
  const r = asRaw(raw);
  const current = asStringArray(r.currentSuggestionTags);
  const proposed = asStringArray(r.proposedSuggestionTags);
  const added = proposed.filter((t) => !current.includes(t));
  const removed = current.filter((t) => !proposed.includes(t));
  return {
    currentSuggestionTags: current, currentIsHealthcare: asBool(r.currentIsHealthcare, false),
    proposedSuggestionTags: proposed, proposedIsHealthcare: asBool(r.proposedIsHealthcare, false),
    addedSuggestionTags: added, removedSuggestionTags: removed, suggestionsChange: added.length > 0 || removed.length > 0,
    naicsVersion: asNullableString(r.naicsVersion),
  };
}

export function industryProfileToWire(input: SetIndustryProfileInput): Raw {
  const one = (s: SetIndustryProfileInput['primary']): Raw => ({
    kind: s.kind, sectorCode: s.sectorCode ?? null, detailCode: s.detailCode ?? null, customDescription: s.customDescription ?? null, healthcareTemplate: s.healthcareTemplate ?? null,
  });
  return { primary: one(input.primary), additional: input.additional.map(one), expectedConcurrencyVersion: input.expectedConcurrencyVersion ?? null };
}
